import { all, call, fork, takeEvery, takeLatest } from 'typed-redux-saga/macro';

import { AV_EUREKA_INIT } from 'Store/av/constants';
import { avSaga } from 'Store/av/logic';

import { GET_LINE_ACCESS_TOKEN } from './account/constants';
import { appInitializeSaga, persistRootSaga } from './app/logic';
import { SEND_TERMS_UPDATE_ACCEPTED } from './appSettings/constants';
import { CAPTCHA_APPEAL_BAN, CAPTCHA_VERIFY_NEW_USER } from './captcha/constants';
import { captchaAppealBanSaga, verifyCaptchaSaga } from './captcha/logic';
import { LOAD_ASYNC_FEATURES } from './dynamicLoading/constants';
import { loadAsyncFeatures } from './dynamicLoading/logic';
import {
  CANCEL_EMAIL_VERIFY,
  EMAIL_COLLECTION_SETTINGS_RECEIVED,
  REMOVE_EMAIL,
  SEND_EMAIL,
  SEND_RECOVERY_EMAIL,
  VERIFY_EMAIL,
} from './email/constants';
import {
  cancelEmailVerifySaga,
  emailCollectionSettingsSaga,
  removeEmailSaga,
  sendEmailSaga,
  sendRecoveryEmailSaga,
  verifyEmailSaga,
} from './email/logic';
import {
  FETCH_EMAIL_SUBSCRIPTIONS,
  UPDATE_EMAIL_SUBSCRIPTIONS,
} from './emailSubscriptionManagement/constants';
import {
  fetchEmailSubscriptionsSaga,
  updateEmailSubscriptionsSaga,
} from './emailSubscriptionManagement/logic';
import { HANDLE_INTENT } from './intent/constants';
import { handleIntentRoutingSaga } from './intent/logic';
import { LOAD_LANGUAGE } from './internationalization/constants';
import { loadLanguageSaga } from './internationalization/logic';
import { FETCH_META } from './meta/constants';
import { fetchMetaSaga } from './meta/logic';
import {
  AUTH_LOGOUT,
  CHECK_AUTH_HEALTH,
  GUEST_LOGIN_START,
  HANDLE_AUTH_RESPONSE,
  LOGIN_START,
  REAUTH,
  RESEND_EMAIL_OTP,
  RESEND_PHONE_OTP,
  SUBMIT_ACCOUNT_RECOVERY_OTP,
  SUBMIT_EMAIL,
  SUBMIT_EMAIL_OTP,
  SUBMIT_FACEBOOK_TOKEN,
  SUBMIT_GOOGLE_TOKEN,
  SUBMIT_LINE_TOKEN,
  SUBMIT_MAGIC_LINK_OTP,
  SUBMIT_PHONE_NUMBER,
  SUBMIT_PHONE_OTP,
} from './mfa/constants';
import {
  authLogoutSaga,
  checkAuthHealthSaga,
  handleAuthResponseSaga,
  handleGuestLoginStartSaga,
  loginStartSaga,
  loginWithLineSaga,
  reauthSaga,
  resendEmailOtpSaga,
  resendPhoneOtpSaga,
  submitAccountRecoveryOtpSaga,
  submitEmailOtpSaga,
  submitEmailSaga,
  submitFacebookTokenSaga,
  submitGoogleTokenSaga,
  submitLineTokenSaga,
  submitMagicLinkOtpSaga,
  submitPhoneNumberSaga,
  submitPhoneOtpSaga,
  submitSMSCaptchaSaga,
} from './mfa/logic';
import { CLAIM_OFFER, SET_OFFER_SEEN } from './offer/constants';
import { claimOfferSaga, setOfferSeenSaga } from './offer/logic';
import { privacyRootSaga } from './privacy/logic';
import { FETCH_PROFILE_SUCCESS } from './profile/constants';
import {
  HANDLE_LOGIN_ROUTE,
  VERIFY_AGE_VERIFICATION_DEEPLINK_ROUTE,
  VERIFY_CAMPAIGN_DEEPLINK_ROUTE,
  VERIFY_CONFIRMATION_DEEPLINK_ROUTE,
  VERIFY_EMAIL_REMOVAL_DEEPLINK_ROUTE,
  VERIFY_EMAIL_SUBSCRIPTION_TOKEN_ROUTE,
  VERIFY_EXPLORE_ROUTE,
  VERIFY_GOLD_DEEPLINK_ROUTE,
  VERIFY_GOLD_ROUTE,
  VERIFY_INCENTIVES_DEEPLINK_ROUTE,
  VERIFY_MERCHANDISING_ROUTE,
  VERIFY_MESSAGES_ROUTE,
  VERIFY_MUTUAL_FRIENDS_ROUTE,
  VERIFY_NAVIGATE_ROUTE,
  VERIFY_PLUS_ROUTE,
  VERIFY_PREFERENCES_ROUTE,
  VERIFY_PROMO_ROUTE,
  VERIFY_SETTINGS_ROUTE,
  VERIFY_WEB_PROFILE_ROUTE,
} from './routing/constants';
import { verifyCampaignDeeplinkRouteSaga } from './routing/deeplink/campaign';
import { verifyConfirmationDeeplinkRouteSaga } from './routing/deeplink/confirm';
import { verifyNavigateDeeplinkRouteSaga } from './routing/deeplink/navigate';
import { verifyEmailRemovalDeeplinkRouteSaga } from './routing/deeplink/removeEmail';
import {
  handleLoginRouteSaga,
  verifyAgeVerificationDeeplinkRouteSaga,
  verifyEmailSubscriptionTokenRouteSaga,
  verifyExploreRouteSaga,
  verifyGoldDeeplinkRouteSaga,
  verifyGoldRouteSaga,
  verifyIncentivesRouteSaga,
  verifyMerchandisingRouteSaga,
  verifyMessagesRouteSaga,
  verifyMutualFriendsRouteSaga,
  verifyPlusRouteSaga,
  verifyPreferencesRouteSaga,
  verifyPromoRouteSaga,
  verifySettingsRouteSaga,
  verifyWebProfileRouteSaga,
} from './routing/verification';
import { sessionRootSaga } from './session/logic';
import { SMS_CAPTCHA_COMPLETED } from './sms/constants';
import { VERIFY_TINDER_U_STATUS } from './tinderU/constants';
import { verifyTinderUStatusSaga } from './tinderU/logic';
import {
  DELETE_ACCOUNT,
  POST_GUEST_LOGIN,
  POST_LOGIN,
  VERIFY_AGE_VERIFICATION_STATUS,
  VERIFY_LOGIN_REDIRECT,
} from './user/constants';
import {
  deleteAccountSaga,
  logoutSaga,
  postGuestLoginSaga,
  postLoginSaga,
  sendTermsUpdateAcceptedSaga,
  setUserDataForAnalytics,
  verifyAgeVerificationStatusSaga,
  verifyLoginRedirectSaga,
} from './user/logic';
import {
  ASYNC_FEATURE_AUTH_ONBOARDING,
  ASYNC_FEATURE_INCENTIVES,
  ASYNC_FEATURE_INTRO_PRICE,
  ASYNC_FEATURE_NOTIFICATIONS,
  ASYNC_FEATURE_PURCHASE,
  ASYNC_FEATURE_SUB_OFFER,
  ASYNC_FEATURE_USER_BIOMETRICS,
  loadAsyncBundle,
} from './asyncSaga';
import { LOGOUT } from './commonActions';
import { analyticsReporterSaga, sparksSaga, TRACK_EVENT, TRACK_SPARKS_EVENT } from './sparks';

const ASYNC_CHUNKS = [
  ASYNC_FEATURE_INCENTIVES,
  ASYNC_FEATURE_INTRO_PRICE,
  ASYNC_FEATURE_PURCHASE,
  ASYNC_FEATURE_SUB_OFFER,
  ASYNC_FEATURE_AUTH_ONBOARDING,
  ASYNC_FEATURE_NOTIFICATIONS,
  ASYNC_FEATURE_USER_BIOMETRICS,
];

export default function* root() {
  yield* fork(persistRootSaga);
  yield* fork(privacyRootSaga);

  yield* all([
    takeLatest(HANDLE_LOGIN_ROUTE, handleLoginRouteSaga),
    takeLatest(VERIFY_AGE_VERIFICATION_DEEPLINK_ROUTE, verifyAgeVerificationDeeplinkRouteSaga),
    takeLatest(VERIFY_CAMPAIGN_DEEPLINK_ROUTE, verifyCampaignDeeplinkRouteSaga),
    takeLatest(VERIFY_CONFIRMATION_DEEPLINK_ROUTE, verifyConfirmationDeeplinkRouteSaga),
    takeLatest(VERIFY_EMAIL_REMOVAL_DEEPLINK_ROUTE, verifyEmailRemovalDeeplinkRouteSaga),
    takeLatest(VERIFY_EMAIL_SUBSCRIPTION_TOKEN_ROUTE, verifyEmailSubscriptionTokenRouteSaga),
    takeLatest(VERIFY_EXPLORE_ROUTE, verifyExploreRouteSaga),
    takeLatest(VERIFY_GOLD_DEEPLINK_ROUTE, verifyGoldDeeplinkRouteSaga),
    takeLatest(VERIFY_GOLD_ROUTE, verifyGoldRouteSaga),
    takeLatest(VERIFY_INCENTIVES_DEEPLINK_ROUTE, verifyIncentivesRouteSaga),
    takeLatest(VERIFY_MERCHANDISING_ROUTE, verifyMerchandisingRouteSaga),
    takeLatest(VERIFY_MESSAGES_ROUTE, verifyMessagesRouteSaga),
    takeLatest(VERIFY_MUTUAL_FRIENDS_ROUTE, verifyMutualFriendsRouteSaga),
    takeLatest(VERIFY_NAVIGATE_ROUTE, verifyNavigateDeeplinkRouteSaga),
    takeLatest(VERIFY_PLUS_ROUTE, verifyPlusRouteSaga),
    takeLatest(VERIFY_PREFERENCES_ROUTE, verifyPreferencesRouteSaga),
    takeLatest(VERIFY_PROMO_ROUTE, verifyPromoRouteSaga),
    takeLatest(VERIFY_SETTINGS_ROUTE, verifySettingsRouteSaga),
    takeLatest(VERIFY_WEB_PROFILE_ROUTE, verifyWebProfileRouteSaga),

    takeEvery(LOAD_LANGUAGE, loadLanguageSaga),

    takeEvery(LOGOUT, logoutSaga),
    takeEvery(DELETE_ACCOUNT, deleteAccountSaga),

    takeEvery(EMAIL_COLLECTION_SETTINGS_RECEIVED, emailCollectionSettingsSaga),
    takeEvery(SEND_RECOVERY_EMAIL, sendRecoveryEmailSaga),

    takeEvery(SEND_EMAIL, sendEmailSaga),
    takeEvery(CANCEL_EMAIL_VERIFY, cancelEmailVerifySaga),
    takeEvery(VERIFY_EMAIL, verifyEmailSaga),
    takeEvery(REMOVE_EMAIL, removeEmailSaga),

    takeEvery(CHECK_AUTH_HEALTH, checkAuthHealthSaga),
    takeEvery(GUEST_LOGIN_START, handleGuestLoginStartSaga),
    takeEvery(LOGIN_START, loginStartSaga),
    takeEvery(RESEND_EMAIL_OTP, resendEmailOtpSaga),
    takeEvery(RESEND_PHONE_OTP, resendPhoneOtpSaga),
    takeEvery(SMS_CAPTCHA_COMPLETED, submitSMSCaptchaSaga),
    takeEvery(SUBMIT_PHONE_NUMBER, submitPhoneNumberSaga),
    takeEvery(SUBMIT_PHONE_OTP, submitPhoneOtpSaga),
    takeEvery(SUBMIT_EMAIL, submitEmailSaga),
    takeEvery(SUBMIT_EMAIL_OTP, submitEmailOtpSaga),
    takeEvery(SUBMIT_FACEBOOK_TOKEN, submitFacebookTokenSaga),
    takeEvery(GET_LINE_ACCESS_TOKEN, loginWithLineSaga),
    takeEvery(SUBMIT_LINE_TOKEN, submitLineTokenSaga),
    takeEvery(SUBMIT_GOOGLE_TOKEN, submitGoogleTokenSaga),
    takeEvery(SUBMIT_ACCOUNT_RECOVERY_OTP, submitAccountRecoveryOtpSaga),
    takeEvery(SUBMIT_MAGIC_LINK_OTP, submitMagicLinkOtpSaga),
    takeEvery(REAUTH, reauthSaga),
    takeEvery(HANDLE_AUTH_RESPONSE, handleAuthResponseSaga),
    takeEvery(CAPTCHA_APPEAL_BAN, captchaAppealBanSaga),
    takeEvery(CAPTCHA_VERIFY_NEW_USER, verifyCaptchaSaga),
    takeEvery(AUTH_LOGOUT, authLogoutSaga),
    takeEvery(POST_GUEST_LOGIN, postGuestLoginSaga),
    takeEvery(POST_LOGIN, postLoginSaga),

    takeEvery(FETCH_META, fetchMetaSaga),

    takeEvery(SEND_TERMS_UPDATE_ACCEPTED, sendTermsUpdateAcceptedSaga),

    takeEvery(TRACK_EVENT, analyticsReporterSaga),
    takeEvery(TRACK_SPARKS_EVENT, sparksSaga),

    takeLatest(FETCH_PROFILE_SUCCESS, setUserDataForAnalytics),

    takeEvery(FETCH_EMAIL_SUBSCRIPTIONS, fetchEmailSubscriptionsSaga),
    takeEvery(UPDATE_EMAIL_SUBSCRIPTIONS, updateEmailSubscriptionsSaga),

    takeLatest(VERIFY_LOGIN_REDIRECT, verifyLoginRedirectSaga),

    takeEvery(VERIFY_TINDER_U_STATUS, verifyTinderUStatusSaga),

    takeEvery(CLAIM_OFFER, claimOfferSaga),
    takeEvery(SET_OFFER_SEEN, setOfferSeenSaga),

    takeEvery(VERIFY_AGE_VERIFICATION_STATUS, verifyAgeVerificationStatusSaga),
    takeEvery(AV_EUREKA_INIT, avSaga),
    takeEvery(HANDLE_INTENT, handleIntentRoutingSaga),
    takeEvery(LOAD_ASYNC_FEATURES, loadAsyncFeatures),
  ]);

  yield* fork(sessionRootSaga);

  yield* call(appInitializeSaga);

  yield* all(ASYNC_CHUNKS.map((name) => call(loadAsyncBundle, name)));
}
